import * as React from 'react';
import { useMemo } from 'react';
import { FaDownload, FaUpload, FaPlus } from 'react-icons/fa';
import { motion } from 'framer-motion';
import ActiveFiltersBar, { ActiveFilter } from '../../../components/ActiveFiltersBar';
import DateRangeFilter, { DateRange } from './DateRangeFilter';
import { Employee } from '../../../hooks/useEmployeeData';
import { WorkArea } from './useTimeTrackings';
import css from './TimeTrackings.module.css';

interface TTHeaderControlsProps {
  workAreas: WorkArea[];
  selectedWAs: number[];
  setSelectedWAs: (ids: number[]) => void;
  isManager: boolean;
  workAreaIds: number[];
  selectedTTIds: Set<number>;
  setShowAdd: (open: boolean) => void;
  setBulkMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  bulkMenuOpen: boolean;
  onBulkChangeWA: () => void;
  onBulkDeleteCRs: () => void;
  onBulkDelete: () => void;
  setExportOpen: (open: boolean) => void;
  openImportModal: () => void;
  dateRange: DateRange | null;
  setDateRange: (range: DateRange | null) => void;
  activeEmployeeId: number | null;
  employees: Employee[];
  handleDateRangeChange: (range: DateRange) => void;
  initialMonth?: Date;
  initialRange?: { start: string; end: string };
}

export function TTHeaderControls({
  workAreas,
  selectedWAs,
  setSelectedWAs,
  isManager,
  workAreaIds,
  selectedTTIds,
  setShowAdd,
  setBulkMenuOpen,
  bulkMenuOpen,
  onBulkChangeWA,
  onBulkDeleteCRs,
  onBulkDelete,
  setExportOpen,
  openImportModal,
  dateRange,
  setDateRange,
  activeEmployeeId,
  employees,
  handleDateRangeChange,
  initialMonth,
  initialRange,
}: TTHeaderControlsProps): React.JSX.Element {
  const activeFilters = useMemo(() => {
    const filters: ActiveFilter[] = [];
    if (selectedWAs.length > 0 && selectedWAs.length < workAreas.length) {
      filters.push({ key: 'wa', label: `${selectedWAs.length} Work Areas`, onClear: () => setSelectedWAs([]) });
    }
    if (dateRange) {
      const fmt = (d: Date) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
      filters.push({ key: 'date', label: `Date: ${fmt(dateRange.start)}–${fmt(dateRange.end)}`, onClear: () => setDateRange(null) });
    }
    if (activeEmployeeId) {
      const emp = employees.find(e => e.id === activeEmployeeId);
      const name = emp ? `${emp.first_name} ${emp.last_name}` : `#${activeEmployeeId}`;
      filters.push({ key: 'employee', label: `Employee: ${name}`, onClear: () => {} });
    }
    return filters;
  }, [selectedWAs, workAreas.length, dateRange, activeEmployeeId, employees, setSelectedWAs, setDateRange]);

  return (
    <>
      <div data-testid="tt-wa-filters" className={css.headerBar}>
        <div className={css.waChips}>
          {workAreas.map(wa => {
            const isSel = selectedWAs.includes(wa.id);
            const isDisabled = isManager && !workAreaIds.includes(wa.id);
            const chipClass = isDisabled
              ? css.waChipDisabled
              : isSel
                ? css.waChipSelected
                : css.waChipDefault;
            return (
              <button
                key={wa.id}
                className={`${css.waChip} ${chipClass}`}
                onClick={() => {
                  if (isDisabled) return;
                  if (isSel) {
                    setSelectedWAs(selectedWAs.filter(x => x !== wa.id));
                  } else {
                    setSelectedWAs([...selectedWAs, wa.id]);
                  }
                }}
                disabled={isDisabled}
              >
                {wa.name}
              </button>
            );
          })}
        </div>
        <div className={css.headerActions}>
          <motion.button
            data-testid="tt-add-button"
            onClick={() => setShowAdd(true)}
            className={css.addButton}
            whileHover={{
              scale: 1.02,
              filter: 'brightness(1.15)',
              boxShadow: '0 4px 12px rgba(163, 172, 230, 0.4)',
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <FaPlus className={css.addButtonIcon} />
            TimeTracking
          </motion.button>
          <div>
            <button
              className={`${css.btnPrimary} ${selectedTTIds.size > 0 ? css.btnPrimaryEnabled : css.btnPrimaryDisabled}`}
              disabled={selectedTTIds.size === 0}
              onClick={() => setBulkMenuOpen(prev => !prev)}
              title={selectedTTIds.size === 0 ? 'Keine Auswahl' : `Aktionen (${selectedTTIds.size})`}
              aria-haspopup="menu"
              aria-expanded={bulkMenuOpen}
            >
              Actions
            </button>
            {bulkMenuOpen && (
              <div
                role="menu"
                className={css.bulkMenu}
                onMouseLeave={() => setBulkMenuOpen(false)}
              >
                <button
                  role="menuitem"
                  className={css.bulkMenuItem}
                  onClick={() => { setBulkMenuOpen(false); onBulkChangeWA(); }}
                >
                  Change Work Area...
                </button>
                <button
                  role="menuitem"
                  className={css.bulkMenuItem}
                  onClick={() => { setBulkMenuOpen(false); onBulkDeleteCRs(); }}
                >
                  Delete Change Requests
                </button>
                <button
                  role="menuitem"
                  className={css.bulkMenuItemDanger}
                  onClick={() => { setBulkMenuOpen(false); onBulkDelete(); }}
                >
                  Delete selected TTs
                </button>
              </div>
            )}
          </div>
          <button
            className={css.iconBtn}
            onClick={() => setExportOpen(true)}
            title="Export Time Trackings"
            aria-label="Export Time Trackings"
          >
            <FaDownload size={16} />
          </button>
          <button
            className={css.iconBtn}
            onClick={openImportModal}
            title="Download/Import TT Form"
            aria-label="Download/Import TT Form"
            data-testid="tt-import-button"
          >
            <FaUpload size={16} />
          </button>
        </div>
      </div>

      <div className={css.filterBarWrap}>
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={() => {
            setSelectedWAs([]);
            setDateRange(null);
          }}
        />
      </div>

      <div data-testid="tt-range-filter" className={css.rangeFilter}>
        <DateRangeFilter
          onRangeChange={handleDateRangeChange}
          defaultMode="thisMonth"
          initialMonth={initialMonth}
          initialRange={initialRange}
        />
      </div>
    </>
  );
}
